import { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, Button, Slider } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";

import { ANIMATION_DEFAULTS } from "config";
import { useMapState } from "contexts/MapStateContext";
import useRun from "hooks/useRun";
import usePrevious from "hooks/usePrevious";

const PlaybackButton = (props) => (
  <Button
    variant="contained"
    color="primary"
    size="small"
    {...props}
    sx={{ marginRight: "8px", flexGrow: 1, ...props.sx }}
  ></Button>
);

export default function PlaybackControl({
  animateLayer,
  animateKey,
  maxAnimationTime = 240,
}) {
  if (!animateLayer) {
    animateLayer = "pathways";
  }
  if (!animateKey) {
    animateKey = "arrival";
  }

  const { map } = useMapState();
  const { currRun } = useRun();

  const [timestep, setTimestep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const animationIntervalRef = useRef(null);
  const prevTimestep = usePrevious(timestep);

  const animationResolution =
    maxAnimationTime * ANIMATION_DEFAULTS.resolutionFactor;

  const onPlayPause = () => {
    if (playing) {
      stopPlaying();
    } else {
      startPlaying();
    }
  };

  const onFastForward = () => {
    setTimestep(maxAnimationTime);
    stopPlaying();
  };

  const onStartOver = () => {
    setTimestep(0);
    stopPlaying();
  };

  const clearAnimationInterval = useCallback(() => {
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }
  }, []);

  const startPlaying = () => {
    setPlaying(true);
    if (timestep >= maxAnimationTime) {
      setTimestep(0);
    }
    startAnimation();
  };

  const stopPlaying = useCallback(() => {
    setPlaying(false);
    clearAnimationInterval();
  }, [setPlaying, clearAnimationInterval]);

  const startAnimation = () => {
    const interval = setInterval(
      animateTimestep,
      ANIMATION_DEFAULTS.secsPerFrame * 1000
    );
    animationIntervalRef.current = interval;
  };

  const animateTimestep = () => {
    setTimestep((prevStep) => {
      const nextStep = prevStep + animationResolution;
      if (nextStep >= maxAnimationTime) {
        stopPlaying();
      }
      return nextStep;
    });
  };

  // Cleanup when unmounted:
  useEffect(() => {
    return () => {
      clearAnimationInterval();
    };
  }, [clearAnimationInterval]);

  // Stop playing/reset if run changes
  useEffect(() => {
    setTimestep(maxAnimationTime);
    stopPlaying();
  }, [currRun, maxAnimationTime, stopPlaying]);

  useEffect(() => {
    if (!map) {
      return;
    }
    if (!map.isStyleLoaded()) {
      return;
    }
    if (!map.getLayer(animateLayer)) {
      return;
    }
    // If the timestep hasn't changed, don't update the filter
    if (prevTimestep === undefined || prevTimestep === timestep) {
      return;
    }

    map.setFilter(animateLayer, [
      "all",
      ["<", ["get", animateKey], timestep],
      [">", ["get", animateKey], 0],
    ]);
  }, [map, timestep, animateKey, animateLayer, prevTimestep]);

  const handleSliderChange = useCallback(
    (event, newValue) => {
      setTimestep(newValue);
      stopPlaying();
    },
    [setTimestep, stopPlaying]
  );

  return (
    <Box mt={2}>
      <Box display="flex">
        <PlaybackButton onClick={onStartOver}>
          <SkipPreviousIcon color="white" />
        </PlaybackButton>
        <PlaybackButton onClick={onPlayPause}>
          {playing ? (
            <PauseIcon color="white" />
          ) : (
            <PlayArrowIcon color="white" />
          )}
        </PlaybackButton>
        <PlaybackButton onClick={onFastForward} sx={{ marginRight: 0 }}>
          <SkipNextIcon color="white" />
        </PlaybackButton>
      </Box>

      <Box display="flex" alignItems="center" pr={1.5} mt={1}>
        <Typography mr={0.5} mt={-1} width="60px">
          {(timestep / 60).toFixed(0)} hours
        </Typography>
        <Box flexGrow={1}>
          <Slider
            min={0}
            max={maxAnimationTime}
            value={timestep}
            onChange={handleSliderChange}
            aria-labelledby="animation-slider"
          />
        </Box>
      </Box>
    </Box>
  );
}
